import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import { elusService } from "services";
import {
  useErrorHandler,
  useSnackbarErrorHandler,
  withComponentErrorBoundary,
} from "utils/errorHandling";
import type { PersonneInfosPrivees, Personne } from "models";
import LoadingScreen from "components/Template/LoadingScreen";
import SaveIcon from "@mui/icons-material/Save";
import { dateUtil } from "@sdeapps/react-core";
import { FormProvider, useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import ControlledTextField from "components/inputs/ControlledTextField";
import { phoneRules, emailRules } from "constants/InputRules";
import EmailsComplementairesListItems from "./EmailsComplementairesListItems";
import { compare } from "fast-json-patch";
import type { Operation } from "fast-json-patch";

function toPatchData(
  eluInfosPrivees: PersonneInfosPrivees,
  initialInfosPrivees: PersonneInfosPrivees
): Array<Operation> {
  // On enlève le potentiel email Complémentaire vide de fin de liste
  eluInfosPrivees.emailsComplementaires = eluInfosPrivees?.emailsComplementaires?.filter(
    (v) => v !== "" && v != null
  );

  return compare(initialInfosPrivees, eluInfosPrivees);
}

interface EluContactInformationsPriveesModificationProps {
  elu: Personne;
  setIsModifying: (m: boolean) => void;
}

function EluContactInformationsPriveesModification({
  elu,
  setIsModifying,
}: Readonly<EluContactInformationsPriveesModificationProps>): ReactNode {
  const [initialInfosPrivees, setInitialInfosPrivees] = useState<PersonneInfosPrivees>();
  const { catchErrors, isLoading } = useErrorHandler();
  const { catchErrors: catchPatchErrors, isLoading: isSending } = useSnackbarErrorHandler();

  const formMethods = useForm<PersonneInfosPrivees>({
    shouldFocusError: false,
  });

  const { handleSubmit, reset } = formMethods;

  const sendData: SubmitHandler<PersonneInfosPrivees> = async function (
    _eluInfosPrivees: PersonneInfosPrivees
  ): Promise<void> {
    await catchPatchErrors(async () => {
      await elusService.patchElu(
        _eluInfosPrivees.id,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        toPatchData(_eluInfosPrivees, initialInfosPrivees!)
      );
      setIsModifying(false);
    });
  };

  useEffect(() => {
    async function getElu(): Promise<void> {
      const _eluInfosPrivees = await elusService.getPersonneInfosPriveesById(elu.id);
      reset(_eluInfosPrivees);
      setInitialInfosPrivees(_eluInfosPrivees);
    }

    void catchErrors(getElu);
  }, [catchErrors, elu, reset]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <Grid container component="form" onSubmit={handleSubmit(sendData)} size={12}>
      <FormProvider {...formMethods}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <List>
            <ListItem>
              <ListItemIcon>
                <CallOutlinedIcon />
              </ListItemIcon>
              <ListItemText>
                <Typography sx={{ fontWeight: "600" }}>Téléphone</Typography>
              </ListItemText>
            </ListItem>
            <ListItem>
              <ListItemText>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <ControlledTextField
                      name="telephonePersonnel"
                      label="Téléphone personnel"
                      rules={phoneRules}
                    />
                  </Grid>
                  <Grid size={12}>
                    <ControlledTextField
                      name="telephonePortable"
                      label="Téléphone portable"
                      rules={phoneRules}
                    />
                  </Grid>
                </Grid>
              </ListItemText>
            </ListItem>
          </List>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <List>
            <ListItem>
              <ListItemIcon>
                <MailOutlinedIcon />
              </ListItemIcon>
              <ListItemText>
                <Typography sx={{ fontWeight: "600" }}>Adresse</Typography>
              </ListItemText>
            </ListItem>
            <ListItem>
              <ListItemText>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <ControlledTextField name="rue" label="Rue" />
                  </Grid>
                  <Grid size={12}>
                    <ControlledTextField name="codePostal" label="Code postal" />
                  </Grid>
                  <Grid size={12}>
                    <ControlledTextField name="commune" label="Commune" />
                  </Grid>
                </Grid>
              </ListItemText>
            </ListItem>
          </List>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <List>
            <ListItem>
              <ListItemIcon>
                <AlternateEmailOutlinedIcon />
              </ListItemIcon>
              <ListItemText>
                <Typography sx={{ fontWeight: "600" }}>E-mail</Typography>
              </ListItemText>
            </ListItem>
            <ListItem>
              <ListItemText>
                <ControlledTextField
                  name="emailPrincipal"
                  label="E-mail principal"
                  defaultHelperText="Attention! Cet e-mail sert d'identifiant pour l'extranet des élus"
                  rules={emailRules}
                />
              </ListItemText>
            </ListItem>
            <EmailsComplementairesListItems />
          </List>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <List>
            <ListItem>
              <Typography>
                Né{elu.sexe === "F" && "e"} le{" "}
                <Typography component="span" sx={{ fontWeight: 600 }}>
                  {dateUtil.format(elu.dateNaissance, "dd/MM/yyyy")}
                </Typography>{" "}
                (
                {Math.floor(
                  dateUtil.differenceInMinutes(undefined, elu.dateNaissance) / 60 / 24 / 365
                )}{" "}
                ans)
              </Typography>
            </ListItem>
          </List>
        </Grid>
        <Grid
          container
          spacing={2}
          sx={{ justifyContent: "flex-end" }}
          size={{ xs: 12, md: 6, lg: 8 }}>
          <Grid>
            <Button
              variant="text"
              color="error"
              onClick={() => {
                setIsModifying(false);
              }}>
              Annuler
            </Button>
          </Grid>
          <Grid>
            <Button
              variant="contained"
              loadingPosition="start"
              type="submit"
              startIcon={<SaveIcon />}
              loading={isSending}>
              Enregistrer
            </Button>
          </Grid>
        </Grid>
      </FormProvider>
    </Grid>
  );
}

const EluContactInformationsPriveesModificationWithErrorBoundary = withComponentErrorBoundary(
  EluContactInformationsPriveesModification
);

export default EluContactInformationsPriveesModificationWithErrorBoundary;
